<script lang="ts" setup>
import CloseIcon from '~/assets/icons/close/closeMobileModal.svg'
import ArrowLeftIcon from '~/assets/icons/arrows/arrowProductSliderLeft.svg'
import ArrowRightIcon from '~/assets/icons/arrows/arrowProductSliderRight.svg'
import type { IImageSlide } from '~/shared/interfaces/ui/sliders';
import type { Swiper } from 'swiper/types'
import { EffectFade } from "swiper/modules";

defineProps<{
  slides: IImageSlide[]
}>();

const emits = defineEmits(['close-slider'])

const prevSlide = ref<HTMLDivElement>()
const nextSlide = ref<HTMLDivElement>()
const fsMainSlider = ref<Swiper>()
const fsThumbSlider = ref<Swiper>()

function closeSlider() {
  emits('close-slider')
}

function setMainSwiper(swiper: Swiper) {
  fsMainSlider.value = swiper;
}

function setThumbsSwiper(swiper: Swiper) {
  fsThumbSlider.value = swiper;
}
</script>

<template>
  <div class="fs-slider">
    <div
      class="fs-slider__close slider-close-btn"
      @click="closeSlider"
    >
      <CloseIcon/>
    </div>
    <Swiper
      ref="fsThumbSlider"
      class="thumb-slider"
      :modules="[SwiperNavigation, SwiperThumbs, SwiperController]"
      :watchSlidesProgress="true"
      :loop="false"
      :breakpoints="{
        320: {
          centerInsufficientSlides: false,
          slidesPerView: 'auto',
          spaceBetween: 8,
          direction: 'horizontal'
        },
        1200: {
          centerInsufficientSlides: true,
          slidesPerView: 5,
          spaceBetween: 8,
          direction: 'vertical'
        }
      }"
      @swiper="setThumbsSwiper"
      v-if="slides.length > 1"
    >
      <SwiperSlide
        v-for="slide in slides"
        :key="`fsThumbSlider_${slide.url}`"
      >
        <nuxt-img
          :src="slide.url"
          :alt="slide.name ? `${slide.name} - купить в интернет-магазине Parts.Sotrans` : 'Изображение товара Parts.Sotrans'"
          width="84"
          height="84"
          loading="lazy"
          quality="60"
        />
      </SwiperSlide>
    </Swiper>
    <Swiper
      class="main-slider"
      ref="fsMainSlider"
      :modules="[SwiperNavigation, SwiperThumbs, SwiperController, SwiperPagination, SwiperKeyboard, EffectFade]"
      :loop="true"
      :slidesPerView=1
      :watchSlidesProgress="true"
      effect="fade"
      :speed="0"
      :keyboard="{
        enabled: true
      }"
      :pagination="{clickable: true}"
      :navigation="{
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }"
      @swiper="setMainSwiper"
      :thumbs="{ swiper: fsThumbSlider}"
      :class="slides.length === 1 && 'one-slide'"
    >
      <SwiperSlide
        v-for="slide in slides"
        :key="`fsMainSlider_${slide.url}`"
      >
        <div class="swiper-slide__image">
          <nuxt-img
            :src="slide.url"
            :alt="slide.name ? `${slide.name} - купить в интернет-магазине Parts.Sotrans` : 'Изображение товара Parts.Sotrans'"
            loading="lazy"
          />
        </div>
      </SwiperSlide>
      <div ref="prevSlide" class="swiper-button-prev svg-stroke-orange">
        <ArrowLeftIcon/>
      </div>
      <div ref="nextSlide" class="swiper-button-next svg-stroke-orange">
        <ArrowRightIcon/>
      </div>
    </Swiper>
  </div>
</template>

<style lang="scss" scoped>
.fs-slider {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  background-color: $bg_page;
  z-index: 1000;
  display: flex;
  align-items: center;

  @include md-breakpoint {
    flex-direction: column-reverse;
    padding: 20px 0;
  }

  &__close {
    position: absolute;
    top: 28px;
    right: 28px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: $control-bg_white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 4px 20px 0 #00000014;

    @include md-breakpoint {
      box-shadow: none;
      width: 24px;
      height: 24px;
      right: 20px;
      top: 20px;
      background-color: transparent;
    }
  }

  .thumb-slider {
    width: 136px;
    height: 452px;
    flex-shrink: 0;
    padding: 0 26px;

    @include md-breakpoint {
      height: auto;
      width: 100%;
      padding: 0 20px;
      display: none;
    }

    .swiper {
      &-wrapper {
        justify-content: center;
        align-items: center;
      }

      &-slide {
        width: 80px;
        height: 80px !important;
        border-radius: 16px;
        border: 2px solid transparent;
        overflow: hidden;
        cursor: pointer;
        transition: border-color .3s;
        display: flex;
        align-items: center;
        justify-content: center;

        @include md-breakpoint {
          width: 64px;
          height: 64px !important;
        }

        &-thumb-active {
          border-color: $content_orange;
        }

        &:hover:not(:active, .swiper-slide-thumb-active) {
          @include mf-breakpoint {
            border-color: $control-content_tretiary;
          }
        }

        img {
          width: 72px;
          height: 72px;
          object-position: center;
          object-fit: cover;
          border-radius: 12px;
        }
      }
    }
  }

  .main-slider {
    padding: 60px 104px;
    border-left: 1px solid $bg_border;
    height: 100%;
    width: calc(100vw - 136px);

    @include md-breakpoint {
      padding: 0;
      height: auto;
      flex-grow: 1;
      width: calc(100vw - 40px);
      margin: 0 auto;
      border: none;
    }

    &.one-slide {
      border: none;

      .swiper-button {

        &-next,
        &-prev {
          display: none;
        }
      }
    }

    :deep(.swiper-pagination) {
      display: none;

      @include md-breakpoint {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;
        bottom: 0;
        height: 24px;
      }

      .swiper-pagination-bullet {
        margin: 0;
        width: 6px;
        height: 6px;
        background-color: $footer_control-bg-grey;
        transition: background-color .3s, width .3s, border-radius .3s;

        &-active {
          background-color: $control-bg_black;
          width: 16px;
          border-radius: 4px;
        }
      }
    }

    .swiper {
      &-button {

        &-next,
        &-prev {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 18px 0 #0000001A;
          border-radius: 50%;
          background-color: $bg_page;

          @include md-breakpoint {
            display: none;
          }

          &:after {
            display: none;
          }

          svg {
            width: auto;
            height: auto;
          }
        }

        &-next {
          right: 28px;
        }

        &-prev {
          left: 28px;
        }
      }

      &-slide {
        height: 100%;
        cursor: grab;

        @include md-breakpoint {
          width: 100%;
          display: flex;
          align-items: center;
        }

        &:active {
          cursor: grabbing;
        }

        &__image {
          max-width: 1282px;
          width: 100%;
          margin: 0 auto;
          display: flex;
          justify-content: center;
          height: 100%;
          background: $white;

          @include md-breakpoint {
            height: 320px;
            max-width: none;
          }

          img {
            object-position: center;
            object-fit: contain;
            aspect-ratio: 16/9;
            max-width: 100%;

            @include md-breakpoint {
              width: 100%;
              height: 100%;
              aspect-ratio: auto;
            }
          }
        }
      }
    }
  }
}
</style>
